import * as cornerstone from "@cornerstonejs/core";

const canvas = document.createElement("canvas");
let lastImageIdDrawn = null;

/**
 * Creates a cornerstone Image object for the specified Image and imageId
 * @param {HTMLImageElement} image - Image object
 * @param {string} imageId - The imageId for this image
 * @returns {object} Cornerstone Image Object
 */
function createImage(image, imageId) {
  const rows = image.naturalHeight;
  const columns = image.naturalWidth;

  function getPixelData() {
    const imageData = getImageData();
    return new Uint8ClampedArray(imageData.data.buffer);
  }

  function getImageData() {
    if (lastImageIdDrawn !== imageId) {
      canvas.width = columns;
      canvas.height = rows;
      const context = canvas.getContext("2d", { willReadFrequently: false });
      context.drawImage(image, 0, 0);
      lastImageIdDrawn = imageId;
    }
    return canvas.getContext("2d", { willReadFrequently: false }).getImageData(0, 0, columns, rows);
  }

  function getCanvas() {
    if (lastImageIdDrawn !== imageId) {
      canvas.width = columns;
      canvas.height = rows;
      const context = canvas.getContext("2d", { willReadFrequently: false });
      context.drawImage(image, 0, 0);
      lastImageIdDrawn = imageId;
    }
    return canvas;
  }

  return {
    imageId,
    minPixelValue: 0,
    maxPixelValue: 255,
    slope: 1,
    intercept: 0,
    windowCenter: 128,
    windowWidth: 255,
    getPixelData,
    getCanvas,
    getImage: () => image,
    rows,
    columns,
    height: rows,
    width: columns,
    color: true,
    rgba: true,
    numberOfComponents: 4,
    bitsAllocated: 8,
    columnPixelSpacing: 1,
    rowPixelSpacing: 1,
    invert: false,
    sizeInBytes: rows * columns * 4,
    photometricInterpretation: "RGB",
  };
}

/**
 * Converts an array buffer into an Image object.
 * @param {ArrayBuffer} arrayBuffer
 * @returns {Promise<HTMLImageElement>}
 */
function arrayBufferToImage(arrayBuffer) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const blob = new Blob([new Uint8Array(arrayBuffer)]);
    const urlCreator = window.URL || window.webkitURL;
    const imageUrl = urlCreator.createObjectURL(blob);

    image.onload = () => {
      resolve(image);
      urlCreator.revokeObjectURL(imageUrl);
    };
    
    image.onerror = (error) => {
      urlCreator.revokeObjectURL(imageUrl);
      reject(error);
    };

    image.src = imageUrl;
  });
}
//
const options = {
  // callback allowing customization of the xhr (e.g. adding custom auth headers, cors, etc)
  beforeSend: (xhr) => {
    // xhr
    xhr.withCredentials = true;
  },
};
/**
 * Loads an image given a URL
 * @param {string} uri - Image URL
 * @param {string} imageId - Image ID
 * @returns {object} { promise, cancelFn }
 */
function loadImage(uri, imageId) {
  const xhr = new XMLHttpRequest();
  xhr.open("GET", uri, true);
  xhr.responseType = "arraybuffer";
  options.beforeSend(xhr);

  const promise = new Promise((resolve, reject) => {
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        arrayBufferToImage(xhr.response)
          .then((image) => resolve(createImage(image, imageId)))
          .catch(reject);
      } else {
        reject(new Error(`Failed to load image: ${xhr.statusText}`));
      }
    };

    xhr.onerror = () => reject(new Error("Network error"));
    xhr.send();
  });

  return { promise, cancelFn: () => xhr.abort() }; // ✅ Always return an object
}

/**
 * Registers the web image loader with cornerstone
 */
function registerWebImageLoader() {
  cornerstone.imageLoader.registerImageLoader("web", (imageId) => {
    const uri = imageId.replace("web:", "");
    return loadImage(uri, imageId); // Returns { promise, cancelFn }
  });
}

export default registerWebImageLoader;
